import type { LocalizedDict } from './index'
import type { MessagingAdminSavedView } from '../api/generated/model/messagingAdminSavedView'
import type { MessagingConversationStatus } from '../api/generated/model/messagingConversationStatus'
import type { MessagingRoutingTeam } from '../api/generated/model/messagingRoutingTeam'
import type { MessagingTopic } from '../api/generated/model/messagingTopic'
import type { MessagingWaitingOn } from '../api/generated/model/messagingWaitingOn'

export type MessagingStrings = {
  navigation: {
    admin: string
    seller: string
    supply: string
  }
  pages: {
    adminTitle: string
    adminDescription: string
    sellerTitle: string
    sellerDescription: string
    supplyTitle: string
    supplyDescription: string
  }
  conversation: {
    status: string
    linkedOrder: string
    linkedWorkOrder: string
    linkedPurchaseOrder: string
    linkedSample: string
    linkedSupport: string
    listLabel: string
    noSelection: string
    noConversations: string
    supportWelcomeTitle: string
    supportWelcomeBody: string
    refresh: string
    retryList: string
    retryMessages: string
    messageHistoryUnavailable: string
  }
  composer: {
    reply: string
    replyTo: string
    internalNote: string
    internalNoteDestination: string
    placeholder: string
    send: string
    sending: string
    retry: string
    failed: string
    readOnly: string
    internalNoteSimulation: string
  }
  attachments: {
    title: string
    addFile: string
    uploadLabel: string
    uploading: string
    uploadFailed: string
    loading: string
    loadFailed: string
    retry: string
    empty: string
    messageFiles: string
    unavailable: string
    unavailableDescription: string
    sampleUnavailable: string
    removeFile: (filename: string) => string
  }
  media: {
    addPhotos: string
    photoInputLabel: string
    photoLimitError: string
    photoTypeError: string
    photoPreviewLabel: (filename: string) => string
    removePhoto: (filename: string) => string
    recordVoiceMessage: string
    requestingMicrophone: string
    microphonePermissionDenied: string
    voiceUnsupported: string
    voiceInterrupted: string
    recording: string
    recordingElapsed: (elapsed: string) => string
    recordingLimitReached: string
    stopRecording: string
    cancelRecording: string
    voicePreview: string
    playVoiceMessage: string
    pauseVoiceMessage: string
    voiceProgress: string
    downloadVoiceMessage: string
    voicePlaybackFailed: string
    removeVoiceMessage: string
    discardVoiceForInternalNote: string
    discardVoiceForInternalNoteDescription: string
    keepVoiceMessage: string
    discardVoiceAndSwitch: string
    uploading: string
    uploadFailed: string
    retryUpload: string
    photoAttachmentLabel: (filename: string) => string
    voiceAttachmentLabel: string
  }
  states: {
    loading: string
    pending: string
    removed: string
    you: string
    pexify: string
    system: string
    unavailableTitle: string
    unavailableDescription: string
    projectionUnavailable: string
    externalCustomerNotice: string
    mediatedSupplyNotice: string
  }
  plannedChannels: {
    title: string
    description: string
    plannedLabel: string
    channels: string[]
    etsyBoundary: string
  }
  entityAction: {
    order: string
    workOrder: string
    opening: string
    error: string
  }
  launcher: {
    title: string
    openHelp: string
    helpOptions: string
    chatChoice: string
    reportChoice: string
    historyChoice: string
    back: string
    messagePexify: string
    supportExplanation: string
    supportPending: string
    supportError: string
    teamSupport: string
    expand: string
    collapse: string
    close: string
    unsentVoiceTitle: string
    unsentVoiceDescription: string
    keepVoice: string
    discardVoiceAndContinue: string
    unread: (count: number) => string
  }
  adminInbox: {
    operationsLabel: string
    savedViews: string
    savedViewsDescription: string
    views: Record<MessagingAdminSavedView, string>
    queue: string
    timeline: string
    context: string
    viewConversationDetails: string
    conversationDetails: string
    backToList: string
    queueError: string
    unread: string
    routingTeam: string
    assignee: string
    unassigned: string
    waitingOn: string
    responseDue: string
    pageSize: string
    sla: Record<'normal' | 'at_risk' | 'breached', string>
    messageErrorTitle: string
    messageErrorDescription: string
    retryMessageHistory: string
    claim: string
    assigneeUserId: string
    applyReassignment: string
    markRead: string
    statusControl: string
    waitingControl: string
    mutationFailed: string
    assignmentConflict: (assignee: string) => string
    simulationNotice: string
    platformViewingReadOnly: string
    sides: Record<'seller' | 'supply', string>
    topics: Record<MessagingTopic, string>
    statuses: Record<MessagingConversationStatus, string>
    waitingValues: Record<MessagingWaitingOn, string>
    teams: Record<MessagingRoutingTeam, string>
    emptyViews: Record<
      MessagingAdminSavedView,
      { title: string; description: string }
    >
  }
}

export const messagingI18n: LocalizedDict<MessagingStrings> = {
  en: {
    navigation: {
      admin: 'Inbox',
      seller: 'Messages',
      supply: 'Messages',
    },
    pages: {
      adminTitle: 'Inbox',
      adminDescription: 'Manage PEXIFY-mediated seller and supply conversations.',
      sellerTitle: 'Messages',
      sellerDescription: 'Contact PEXIFY about your orders and account.',
      supplyTitle: 'Messages',
      supplyDescription: 'Contact PEXIFY about authorized production work.',
    },
    conversation: {
      status: 'Status',
      linkedOrder: 'Linked order',
      linkedWorkOrder: 'Linked work order',
      linkedPurchaseOrder: 'Linked purchase order',
      linkedSample: 'Linked sample',
      linkedSupport: 'PEXIFY Support',
      listLabel: 'Conversations',
      noSelection: 'Select a conversation to view its messages.',
      noConversations: 'No conversations yet.',
      supportWelcomeTitle: 'Hi there',
      supportWelcomeBody: 'You are speaking with PEXIFY. How can we help?',
      refresh: 'Refresh',
      retryList: 'Retry conversations',
      retryMessages: 'Retry messages',
      messageHistoryUnavailable: 'Message history could not be loaded.',
    },
    composer: {
      reply: 'Reply',
      replyTo: 'Reply to',
      internalNote: 'Internal note',
      internalNoteDestination: 'Visible only to the PEXIFY team',
      placeholder: 'Write a message',
      send: 'Send',
      sending: 'Sending…',
      retry: 'Try again',
      failed: 'Message could not be sent.',
      readOnly: 'This conversation is currently read-only.',
      internalNoteSimulation:
        'Visibility is simulated for the routing team, participants, and authorized managers; this is not production security.',
    },
    attachments: {
      title: 'Drive attachments',
      addFile: 'Add from Drive',
      uploadLabel: 'Upload a Drive file',
      uploading: 'Uploading…',
      uploadFailed: 'The file could not be uploaded. Your draft and selections were kept.',
      loading: 'Loading Drive files…',
      loadFailed: 'Drive files could not be loaded.',
      retry: 'Retry Drive files',
      empty: 'No Drive files are attached to this work yet.',
      messageFiles: 'Message attachments',
      unavailable: 'Attachment unavailable',
      unavailableDescription:
        'The file is missing, no longer available, or you do not have access.',
      sampleUnavailable:
        'Drive attachments are not available for sample conversations yet.',
      removeFile: (filename) => `Remove ${filename}`,
    },
    media: {
      addPhotos: 'Add photos',
      photoInputLabel: 'Choose photos to add',
      photoLimitError: 'You can add up to four photos to one message.',
      photoTypeError: 'Choose a JPEG, PNG, or WebP photo.',
      photoPreviewLabel: (filename) => `Photo preview: ${filename}`,
      removePhoto: (filename) => `Remove ${filename}`,
      recordVoiceMessage: 'Record voice message',
      requestingMicrophone: 'Requesting microphone access…',
      microphonePermissionDenied:
        'Microphone access was denied. Allow access in your browser and try again.',
      voiceUnsupported:
        'Voice recording is not supported in this browser. You can still send text, photos, or Drive files.',
      voiceInterrupted: 'Recording was interrupted. Try recording again.',
      recording: 'Recording voice message',
      recordingElapsed: (elapsed) => `Recording: ${elapsed}`,
      recordingLimitReached: 'The two-minute recording limit was reached.',
      stopRecording: 'Stop recording',
      cancelRecording: 'Cancel recording',
      voicePreview: 'Voice message preview',
      playVoiceMessage: 'Play voice message',
      pauseVoiceMessage: 'Pause voice message',
      voiceProgress: 'Voice message progress',
      downloadVoiceMessage: 'Download voice message',
      voicePlaybackFailed: 'Voice message could not be played.',
      removeVoiceMessage: 'Remove voice message',
      discardVoiceForInternalNote: 'Discard voice message?',
      discardVoiceForInternalNoteDescription:
        'Internal notes cannot include voice. Keep the recording or discard it before switching.',
      keepVoiceMessage: 'Keep voice message',
      discardVoiceAndSwitch: 'Discard and switch',
      uploading: 'Uploading…',
      uploadFailed: 'Upload failed. Your media is still available to retry.',
      retryUpload: 'Retry upload',
      photoAttachmentLabel: (filename) => `Photo attachment: ${filename}`,
      voiceAttachmentLabel: 'Voice message attachment',
    },
    states: {
      loading: 'Loading messages…',
      pending: 'Sending',
      removed: 'Message removed',
      you: 'You',
      pexify: 'PEXIFY',
      system: 'System',
      unavailableTitle: 'Messaging is not available',
      unavailableDescription:
        'This workspace does not currently have access to Messaging.',
      projectionUnavailable:
        'Some conversation data could not be displayed.',
      externalCustomerNotice:
        'Customer messages stay in the marketplace. Use PEXIFY Messages only to contact PEXIFY.',
      mediatedSupplyNotice:
        'Supply conversations are mediated by PEXIFY and show only authorized work-order context.',
    },
    plannedChannels: {
      title: 'Planned capabilities',
      description:
        'These messaging capabilities are planned. Each requires a separately approved delivery process.',
      plannedLabel: 'Planned',
      channels: [
        'Team Chat',
        'Support Case',
        'WhatsApp Business',
        'Telegram',
      ],
      etsyBoundary:
        'Etsy buyer messages stay on Etsy and do not enter PEXIFY Messaging.',
    },
    entityAction: {
      order: 'Contact PEXIFY about this order',
      workOrder: 'Contact PEXIFY about this work order',
      opening: 'Opening…',
      error: 'Conversation could not be opened.',
    },
    launcher: {
      title: 'Messages',
      openHelp: 'Open help',
      helpOptions: 'Help options',
      chatChoice: 'Chat',
      reportChoice: 'Report a problem',
      historyChoice: 'My reports',
      back: 'Back to conversations',
      messagePexify: 'Message PEXIFY',
      supportExplanation:
        'Ask PEXIFY a general question without linking an order or work item.',
      supportPending: 'Opening PEXIFY Support…',
      supportError: 'Could not open PEXIFY Support. Try again.',
      teamSupport: 'PEXIFY team · Support',
      expand: 'Expand messaging',
      collapse: 'Collapse messaging',
      close: 'Close messaging',
      unsentVoiceTitle: 'Discard voice message?',
      unsentVoiceDescription:
        'Your recording has not been sent. Keep it here or discard it before leaving this conversation.',
      keepVoice: 'Keep recording',
      discardVoiceAndContinue: 'Discard and continue',
      unread: (count) => `${count} unread messages`,
    },
    adminInbox: {
      operationsLabel: 'PEXIFY Operations',
      savedViews: 'Saved views',
      savedViewsDescription: 'Choose an operational queue.',
      views: {
        assigned_to_me: 'Assigned to me',
        unassigned: 'Unassigned',
        my_team: 'My team',
        seller: 'Seller',
        supply: 'Supply',
        sla_risk: 'SLA risk',
        waiting_on_pexify: 'Waiting on PEXIFY',
      },
      queue: 'Conversation queue',
      timeline: 'Message timeline',
      context: 'Conversation context',
      viewConversationDetails: 'View conversation details',
      conversationDetails: 'Conversation details',
      backToList: 'Back to conversations',
      queueError: 'The conversation queue could not be loaded.',
      unread: 'unread',
      routingTeam: 'Routing team',
      assignee: 'Assignee',
      unassigned: 'Unassigned',
      waitingOn: 'Waiting on',
      responseDue: 'Response due',
      pageSize: 'Rows per page',
      sla: {
        normal: 'SLA normal',
        at_risk: 'SLA at risk',
        breached: 'SLA breached',
      },
      messageErrorTitle: 'Message history could not be loaded',
      messageErrorDescription: 'Try loading this conversation history again.',
      retryMessageHistory: 'Retry message history',
      claim: 'Claim',
      assigneeUserId: 'PEXIFY user ID',
      applyReassignment: 'Apply reassignment',
      markRead: 'Mark as read',
      statusControl: 'Conversation status',
      waitingControl: 'Waiting on',
      mutationFailed: 'The operation could not be completed. Try again.',
      assignmentConflict: (assignee) =>
        `${assignee} claimed this conversation first. The queue has been refreshed.`,
      simulationNotice:
        'Operational controls and internal-note visibility are simulated. They do not prove production authorization or delivery.',
      platformViewingReadOnly:
        'Platform viewing is read-only. Exit the organization view to make changes.',
      sides: { seller: 'Seller', supply: 'Supply' },
      topics: {
        general: 'General', order: 'Order', production: 'Production',
        sample: 'Sample', shipment: 'Shipment', quality: 'Quality',
      },
      statuses: {
        open: 'Open', pending: 'Pending', resolved: 'Resolved', snoozed: 'Snoozed',
      },
      waitingValues: {
        pexify: 'PEXIFY', counterpart: 'Counterpart', nobody: 'Nobody',
      },
      teams: {
        seller_support: 'Seller support',
        supply_operations: 'Supply operations',
      },
      emptyViews: {
        assigned_to_me: { title: 'Nothing assigned to you', description: 'New work assigned to you will appear here.' },
        unassigned: { title: 'No unassigned conversations', description: 'Every conversation currently has an owner.' },
        my_team: { title: 'No conversations for your teams', description: 'Team-routed work will appear here.' },
        seller: { title: 'No seller conversations', description: 'Seller conversations matching this view will appear here.' },
        supply: { title: 'No supply conversations', description: 'Supply conversations matching this view will appear here.' },
        sla_risk: { title: 'No conversations at SLA risk', description: 'There are no at-risk or breached obligations.' },
        waiting_on_pexify: { title: 'Nothing waiting on PEXIFY', description: 'No counterpart is currently waiting for a PEXIFY response.' },
      },
    },
  },
  tr: {
    navigation: {
      admin: 'Gelen Kutusu',
      seller: 'Mesajlar',
      supply: 'Mesajlar',
    },
    pages: {
      adminTitle: 'Gelen Kutusu',
      adminDescription:
        'PEXIFY aracılı satıcı ve tedarik görüşmelerini yönetin.',
      sellerTitle: 'Mesajlar',
      sellerDescription:
        'Siparişleriniz ve hesabınız hakkında PEXIFY ile iletişime geçin.',
      supplyTitle: 'Mesajlar',
      supplyDescription:
        'Yetkili üretim işleri hakkında PEXIFY ile iletişime geçin.',
    },
    conversation: {
      status: 'Durum',
      linkedOrder: 'Bağlı sipariş',
      linkedWorkOrder: 'Bağlı iş emri',
      linkedPurchaseOrder: 'Bağlı satın alma siparişi',
      linkedSample: 'Bağlı numune',
      linkedSupport: 'PEXIFY Destek',
      listLabel: 'Görüşmeler',
      noSelection: 'Mesajları görmek için bir görüşme seçin.',
      noConversations: 'Henüz görüşme yok.',
      supportWelcomeTitle: 'Merhaba',
      supportWelcomeBody: 'PEXIFY ile görüşüyorsunuz. Size nasıl yardımcı olabiliriz?',
      refresh: 'Yenile',
      retryList: 'Görüşmeleri yeniden dene',
      retryMessages: 'Mesajları yeniden dene',
      messageHistoryUnavailable: 'Mesaj geçmişi yüklenemedi.',
    },
    composer: {
      reply: 'Yanıt',
      replyTo: 'Yanıt hedefi',
      internalNote: 'İç not',
      internalNoteDestination: 'Yalnızca PEXIFY ekibi görebilir',
      placeholder: 'Mesaj yazın',
      send: 'Gönder',
      sending: 'Gönderiliyor…',
      retry: 'Tekrar dene',
      failed: 'Mesaj gönderilemedi.',
      readOnly: 'Bu görüşme şu anda salt okunurdur.',
      internalNoteSimulation:
        'Görünürlük yönlendirme takımı, katılımcılar ve yetkili yöneticiler için simüle edilir; bu, canlı ortam güvenliği kanıtı değildir.',
    },
    attachments: {
      title: 'Drive ekleri',
      addFile: "Drive'dan ekle",
      uploadLabel: 'Drive dosyası yükle',
      uploading: 'Yükleniyor…',
      uploadFailed: 'Dosya yüklenemedi. Taslağınız ve seçimleriniz korundu.',
      loading: 'Drive dosyaları yükleniyor…',
      loadFailed: 'Drive dosyaları yüklenemedi.',
      retry: 'Drive dosyalarını yeniden dene',
      empty: 'Bu işe bağlı Drive dosyası henüz yok.',
      messageFiles: 'Mesaj ekleri',
      unavailable: 'Ek kullanılamıyor',
      unavailableDescription:
        'Dosya eksik, artık kullanılamıyor veya erişim yetkiniz yok.',
      sampleUnavailable:
        'Numune görüşmelerinde Drive ekleri henüz kullanılamıyor.',
      removeFile: (filename) => `${filename} dosyasını kaldır`,
    },
    media: {
      addPhotos: 'Fotoğraf ekle',
      photoInputLabel: 'Eklenecek fotoğrafları seç',
      photoLimitError: 'Bir mesaja en fazla dört fotoğraf ekleyebilirsiniz.',
      photoTypeError: 'JPEG, PNG veya WebP biçiminde bir fotoğraf seçin.',
      photoPreviewLabel: (filename) => `Fotoğraf önizlemesi: ${filename}`,
      removePhoto: (filename) => `${filename} fotoğrafını kaldır`,
      recordVoiceMessage: 'Sesli mesaj kaydet',
      requestingMicrophone: 'Mikrofon erişimi isteniyor…',
      microphonePermissionDenied:
        'Mikrofon erişimi reddedildi. Tarayıcınızdan izin verip tekrar deneyin.',
      voiceUnsupported:
        'Bu tarayıcı ses kaydını desteklemiyor. Metin, fotoğraf veya Drive dosyası göndermeye devam edebilirsiniz.',
      voiceInterrupted: 'Kayıt kesintiye uğradı. Tekrar kaydetmeyi deneyin.',
      recording: 'Sesli mesaj kaydediliyor',
      recordingElapsed: (elapsed) => `Kayıt: ${elapsed}`,
      recordingLimitReached: 'İki dakikalık kayıt sınırına ulaşıldı.',
      stopRecording: 'Kaydı durdur',
      cancelRecording: 'Kaydı iptal et',
      voicePreview: 'Sesli mesaj önizlemesi',
      playVoiceMessage: 'Sesli mesajı oynat',
      pauseVoiceMessage: 'Sesli mesajı duraklat',
      voiceProgress: 'Sesli mesaj ilerlemesi',
      downloadVoiceMessage: 'Sesli mesajı indir',
      voicePlaybackFailed: 'Sesli mesaj oynatılamadı.',
      removeVoiceMessage: 'Sesli mesajı kaldır',
      discardVoiceForInternalNote: 'Sesli mesaj silinsin mi?',
      discardVoiceForInternalNoteDescription:
        'İç notlara ses eklenemez. Kaydı koruyun veya iç nota geçmeden önce silin.',
      keepVoiceMessage: 'Sesli mesajı koru',
      discardVoiceAndSwitch: 'Sil ve geç',
      uploading: 'Yükleniyor…',
      uploadFailed: 'Yükleme başarısız oldu. Medyanız yeniden denemek için korundu.',
      retryUpload: 'Yüklemeyi yeniden dene',
      photoAttachmentLabel: (filename) => `Fotoğraf eki: ${filename}`,
      voiceAttachmentLabel: 'Sesli mesaj eki',
    },
    states: {
      loading: 'Mesajlar yükleniyor…',
      pending: 'Gönderiliyor',
      removed: 'Mesaj kaldırıldı',
      you: 'Siz',
      pexify: 'PEXIFY',
      system: 'Sistem',
      unavailableTitle: 'Mesajlaşma kullanılamıyor',
      unavailableDescription:
        'Bu çalışma alanının şu anda Mesajlaşma erişimi yok.',
      projectionUnavailable:
        'Bazı görüşme verileri güvenli şekilde görüntülenemedi.',
      externalCustomerNotice:
        'Müşteri mesajları pazaryerinde kalır. PEXIFY Mesajlar alanını yalnızca PEXIFY ile iletişim için kullanın.',
      mediatedSupplyNotice:
        'Tedarik görüşmelerine PEXIFY aracılık eder ve yalnızca yetkili iş emri bağlamı gösterilir.',
    },
    plannedChannels: {
      title: 'Planlanan yetenekler',
      description:
        'Bu mesajlaşma yetenekleri planlanmıştır. Her biri ayrı bir onaylı geliştirme süreci gerektirir.',
      plannedLabel: 'Planlandı',
      channels: [
        'Ekip Sohbeti',
        'Destek Kaydı',
        'WhatsApp Business',
        'Telegram',
      ],
      etsyBoundary:
        'Etsy alıcı mesajları Etsy’de kalır ve PEXIFY Mesajlaşma alanına girmez.',
    },
    entityAction: {
      order: 'Bu sipariş hakkında PEXIFY ile görüş',
      workOrder: 'Bu iş emri hakkında PEXIFY ile görüş',
      opening: 'Açılıyor…',
      error: 'Görüşme açılamadı.',
    },
    launcher: {
      title: 'Mesajlar',
      openHelp: 'Yardımı aç',
      helpOptions: 'Yardım seçenekleri',
      chatChoice: 'Sohbet',
      reportChoice: 'Hata bildirimi',
      historyChoice: 'Raporlarım',
      back: 'Görüşmelere dön',
      messagePexify: "PEXIFY'a Mesaj Gönder",
      supportExplanation:
        "Bir sipariş veya iş kaydı bağlamadan PEXIFY'a genel bir soru sorun.",
      supportPending: 'PEXIFY Destek açılıyor…',
      supportError: 'PEXIFY Destek açılamadı. Tekrar deneyin.',
      teamSupport: 'PEXIFY ekibi · Destek',
      expand: 'Mesajlaşmayı genişlet',
      collapse: 'Mesajlaşmayı küçült',
      close: 'Mesajlaşmayı kapat',
      unsentVoiceTitle: 'Sesli mesaj silinsin mi?',
      unsentVoiceDescription:
        'Kaydınız henüz gönderilmedi. Bu görüşmede koruyun veya ayrılmadan önce silin.',
      keepVoice: 'Kaydı koru',
      discardVoiceAndContinue: 'Sil ve devam et',
      unread: (count) => `${count} okunmamış mesaj`,
    },
    adminInbox: {
      operationsLabel: 'PEXIFY Operasyonları',
      savedViews: 'Kayıtlı görünümler',
      savedViewsDescription: 'Operasyon kuyruğunu seçin.',
      views: {
        assigned_to_me: 'Bana atananlar',
        unassigned: 'Atanmamış',
        my_team: 'Takımım',
        seller: 'Satıcı',
        supply: 'Tedarik',
        sla_risk: 'SLA riski',
        waiting_on_pexify: 'PEXIFY bekliyor',
      },
      queue: 'Görüşme kuyruğu',
      timeline: 'Mesaj zaman akışı',
      context: 'Görüşme bağlamı',
      viewConversationDetails: 'Görüşme ayrıntılarını gör',
      conversationDetails: 'Görüşme ayrıntıları',
      backToList: 'Görüşmelere dön',
      queueError: 'Görüşme kuyruğu yüklenemedi.',
      unread: 'okunmamış',
      routingTeam: 'Yönlendirme takımı',
      assignee: 'Atanan kişi',
      unassigned: 'Atanmamış',
      waitingOn: 'Yanıt sırası',
      responseDue: 'Yanıt son zamanı',
      pageSize: 'Sayfa başına satır',
      sla: {
        normal: 'SLA normal',
        at_risk: 'SLA riskte',
        breached: 'SLA aşıldı',
      },
      messageErrorTitle: 'Mesaj geçmişi yüklenemedi',
      messageErrorDescription: 'Bu görüşmenin geçmişini yeniden yüklemeyi deneyin.',
      retryMessageHistory: 'Mesaj geçmişini yeniden dene',
      claim: 'Üzerime al',
      assigneeUserId: 'PEXIFY kullanıcı kimliği',
      applyReassignment: 'Atamayı uygula',
      markRead: 'Okundu işaretle',
      statusControl: 'Görüşme durumu',
      waitingControl: 'Yanıt sırası',
      mutationFailed: 'İşlem tamamlanamadı. Yeniden deneyin.',
      assignmentConflict: (assignee) =>
        `${assignee} bu görüşmeyi önce üzerine aldı. Kuyruk yenilendi.`,
      simulationNotice:
        'Operasyon kontrolleri ve iç not görünürlüğü simülasyondur. Canlı ortam yetkilendirmesini veya teslimatı kanıtlamaz.',
      platformViewingReadOnly:
        'Platform görüntüleme salt okunurdur. Değişiklik yapmak için organizasyon görünümünden çıkın.',
      sides: { seller: 'Satıcı', supply: 'Tedarik' },
      topics: {
        general: 'Genel', order: 'Sipariş', production: 'Üretim',
        sample: 'Numune', shipment: 'Sevkiyat', quality: 'Kalite',
      },
      statuses: {
        open: 'Açık', pending: 'Beklemede', resolved: 'Çözüldü', snoozed: 'Ertelendi',
      },
      waitingValues: {
        pexify: 'PEXIFY', counterpart: 'Karşı taraf', nobody: 'Kimse',
      },
      teams: {
        seller_support: 'Satıcı desteği',
        supply_operations: 'Tedarik operasyonları',
      },
      emptyViews: {
        assigned_to_me: { title: 'Size atanmış görüşme yok', description: 'Size atanan yeni işler burada görünür.' },
        unassigned: { title: 'Atanmamış görüşme yok', description: 'Tüm görüşmelerin şu anda bir sahibi var.' },
        my_team: { title: 'Takımlarınız için görüşme yok', description: 'Takıma yönlendirilen işler burada görünür.' },
        seller: { title: 'Satıcı görüşmesi yok', description: 'Bu görünüme uyan satıcı görüşmeleri burada görünür.' },
        supply: { title: 'Tedarik görüşmesi yok', description: 'Bu görünüme uyan tedarik görüşmeleri burada görünür.' },
        sla_risk: { title: 'SLA riski taşıyan görüşme yok', description: 'Riskte veya aşılmış yükümlülük bulunmuyor.' },
        waiting_on_pexify: { title: 'PEXIFY yanıtı bekleyen yok', description: 'Şu anda PEXIFY yanıtı bekleyen karşı taraf yok.' },
      },
    },
  },
}
